// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useMemo } from 'react';

import type { TableViewId, TableViewType } from '@evolvemep/api-ts';
import { Flex, LoadingOverlay, Text } from '@mantine/core';
import type { GridApi, SideBarDef } from 'ag-grid-enterprise';

import { ExportGridButton, ExportPaginatedGridButton } from 'helpers/ag-grid/Exports/ExportGridButton';
import { TableViewPanel } from 'helpers/ag-grid/TableViewPanel/TableViewPanel';
import { isNotNil } from 'helpers/isNotNil';

type Props = {
  gridApi: GridApi | null;
  tableName: string;
  tableViewType?: TableViewType;
  initialTableViewId?: TableViewId | null;
  loadingInitialTableView?: boolean;
  showFiltersPanel?: boolean;
  exportFileName?: string;
  paginatedExport?: boolean;
};

export const useSideBar = ({
  gridApi,
  tableName,
  tableViewType,
  loadingInitialTableView,
  showFiltersPanel = true,
  exportFileName,
  paginatedExport = true,
}: Props) =>
  useMemo<SideBarDef>(() => {
    const toolPanels: SideBarDef['toolPanels'] = [];
    toolPanels.push({
      id: 'columns',
      labelDefault: 'Columns',
      labelKey: 'columns',
      iconKey: 'columns',
      // See docs: https://www.ag-grid.com/react-data-grid/tool-panel-columns/
      toolPanel: 'agColumnsToolPanel',
      toolPanelParams: {
        suppressRowGroups: true,
        suppressValues: true,
        suppressPivotMode: true,
      },
    });
    if (showFiltersPanel) {
      toolPanels.push({
        id: 'filters',
        labelDefault: 'Filters',
        labelKey: 'filters',
        iconKey: 'filter',
        // See docs: https://www.ag-grid.com/react-data-grid/tool-panel-filters-new/
        toolPanel: 'agNewFiltersToolPanel',
      });
    }
    if (isNotNil(tableViewType)) {
      toolPanels.push({
        id: 'tableViews',
        labelDefault: 'Table Views',
        labelKey: 'tableViews',
        iconKey: 'tableViews',
        minWidth: 170,
        maxWidth: 300,
        toolPanel:
          gridApi && !loadingInitialTableView
            ? () => <TableViewPanel gridApi={gridApi} tableViewType={tableViewType} />
            : () => <LoadingOverlay visible={true} />,
      });
    }
    toolPanels.push({
      id: 'export',
      labelDefault: 'Export',
      labelKey: 'export',
      iconKey: 'export',
      toolPanel: () => (
        <Flex direction="column" p="md" gap="md">
          <Text fw={500}>Export Data</Text>
          <Text size="sm" c="dimmed">
            Export the full dataset as a file. Note that any filters or sorts applied will be respected.
          </Text>
          {paginatedExport ? (
            <ExportPaginatedGridButton
              fileName={exportFileName ?? tableName}
              size="sm"
              getGridApi={() => gridApi}
              disabled={loadingInitialTableView}
              confirm={false}
              fullWidth
            />
          ) : (
            <ExportGridButton
              fileName={exportFileName ?? tableName}
              size="sm"
              gridApi={gridApi}
              disabled={loadingInitialTableView}
              confirm={false}
              fullWidth
            />
          )}
        </Flex>
      ),
    });

    return {
      toolPanels,
      position: 'left',
    };
  }, [showFiltersPanel, tableViewType, gridApi, loadingInitialTableView, paginatedExport, exportFileName, tableName]);
